import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { DataTable } from '../components/DataTable';
import { LayoutGrid, List, Download, GripVertical, RotateCcw, Copy, Eye, Maximize2, MessageSquare, Clock, User, AlertTriangle, FileText, Briefcase, CheckSquare, Square, Plus, XCircle, BarChart3, CheckCheck } from 'lucide-react';
import { getSeverityColor } from '../utils/styleUtils';
import { AlertsDashboard } from '../components/AlertsDashboard';
import { AlertBatchActions } from '../components/AlertBatchActions';
import { ExcelExportButton } from '../components/ExcelExportButton';
import { AlertsDialogs } from '../components/alerts/AlertsDialogs';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Componente para tarjeta arrastrable
const DraggableAlertCard = ({ alert, onReopen, onDuplicate, onViewDetail, onCreateCase, isSelected, onToggleSelect }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: alert.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
    borderColor: isSelected ? '#3b82f6' : undefined,
  };

  const isActive = !['CERRADA', 'DESCARTADA'].includes(alert.estado);

  const isClosed = alert.estado === 'CERRADA' || alert.estado === 'DESCARTADA';

  return (
    <Card
      ref={setNodeRef}
      style={style}
      data-testid={`alert-card-${alert.id}`}
      className={`p-3 mb-3 border ${isSelected ? 'border-blue-400 ring-1 ring-blue-300' : 'border-border'} overflow-hidden ${isDragging ? 'shadow-lg ring-2 ring-blue-500' : 'hover:shadow-md'} transition-all duration-200`}
    >
      <div className="flex items-start gap-2">
        {/* Checkbox de selección */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(alert.id);
          }}
          className="flex-shrink-0 p-0.5 hover:bg-slate-100 rounded transition-colors"
          title={isSelected ? 'Deseleccionar' : 'Seleccionar para crear caso'}
        >
          {isSelected ? (
            <CheckSquare size={16} className="text-blue-600" />
          ) : (
            <Square size={16} className="text-slate-400" />
          )}
        </button>
        
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-slate-400 hover:text-slate-600 flex-shrink-0"
        >
          <GripVertical size={14} />
        </div>
        
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-start justify-between gap-1 mb-1">
            <div className="flex items-center gap-1">
              <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-semibold flex-shrink-0 ${getSeverityColor(alert.severidad)}`}>
                {alert.severidad}
              </span>
              {alert.estado === 'PENDIENTE_APROBACION' && (
                <span className="inline-flex items-center" title="Esperando aprobación">
                  <Clock size={12} className="text-amber-600 animate-pulse" />
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0"
              onClick={(e) => {
                e.stopPropagation();
                onViewDetail(alert);
              }}
              data-testid={`view-alert-${alert.id}`}
            >
              <Eye size={12} className="text-slate-400 hover:text-blue-600" />
            </Button>
          </div>
          
          <h3 
            className="font-medium text-slate-900 mb-1 text-xs line-clamp-2 cursor-pointer hover:text-blue-600 break-words"
            onClick={() => onViewDetail(alert)}
          >
            {alert.titulo}
          </h3>
          <p className="text-xs text-slate-600 line-clamp-2 break-words">{alert.descripcion}</p>
          
          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
            <span className="font-mono">{new Date(alert.created_at).toLocaleDateString()}</span>
            {alert.cuenta_id && (
              <>
                <span>•</span>
                <span className="truncate max-w-[120px]" title={alert.cuenta_id}>
                  {alert.razon_social || alert.cuenta_id}
                </span>
              </>
            )}
          </div>

          {/* Acciones para alertas activas - Crear Caso */}
          {isActive && (
            <div className="mt-2 pt-2 border-t border-slate-100">
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateCase(alert);
                }}
                className="text-xs h-6 px-2 w-full"
                style={{ color: '#059669', borderColor: '#a7f3d0', fontSize: '10px' }}
                data-testid={`create-case-from-alert-${alert.id}`}
              >
                <Briefcase size={10} className="mr-1" style={{ color: '#059669' }} />
                <span style={{ color: '#059669' }}>Crear Caso</span>
              </Button>
            </div>
          )}

          {/* Acciones para alertas cerradas */}
          {isClosed && (
            <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onReopen(alert);
                }}
                className="text-xs h-6 px-2"
                style={{ color: '#2563eb', borderColor: '#bfdbfe', fontSize: '10px' }}
              >
                <RotateCcw size={10} className="mr-1" style={{ color: '#2563eb' }} />
                <span style={{ color: '#2563eb' }}>Reabrir</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate(alert);
                }}
                className="text-xs h-6 px-2"
                style={{ color: '#9333ea', borderColor: '#e9d5ff', fontSize: '10px' }}
              >
                <Copy size={10} className="mr-1" style={{ color: '#9333ea' }} />
                <span style={{ color: '#9333ea' }}>Nueva</span>
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

// Componente columna del Kanban
const KanbanColumn = ({ column, alerts, onReopen, onDuplicate, onViewDetail, onCreateCase, selectedAlerts, onToggleSelect }) => {
  return (
    <div className="flex flex-col min-h-[400px]">
      <div
        className={`p-3 rounded-t-lg border ${column.color} sticky top-0 z-10`}
      >
        <h2 className="text-sm font-heading font-bold text-slate-900">
          {column.label}
        </h2>
        <p className="text-xs text-slate-600 mt-1">
          {alerts.length} alertas
        </p>
      </div>
      <div 
        className="flex-1 bg-slate-50 border border-t-0 border-slate-200 rounded-b-lg p-3 overflow-y-auto"
        data-testid={`kanban-column-${column.key}`}
      >
        <SortableContext
          items={alerts.map(a => a.id)}
          strategy={verticalListSortingStrategy}
        >
          {alerts.map((alert) => (
            <DraggableAlertCard 
              key={alert.id} 
              alert={alert} 
              onReopen={onReopen}
              onDuplicate={onDuplicate}
              onViewDetail={onViewDetail}
              onCreateCase={onCreateCase}
              isSelected={selectedAlerts.includes(alert.id)}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </SortableContext>
        {alerts.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-4">
            Arrastra alertas aquí
          </p>
        )}
      </div>
    </div>
  );
};

export const Alerts = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [activeId, setActiveId] = useState(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [filterMonitor, setFilterMonitor] = useState('all');
  
  // Filtros
  const [filterUser, setFilterUser] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchAlertText, setSearchAlertText] = useState('');
  
  // Modal de vista rápida
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [alertComments, setAlertComments] = useState([]);
  
  // Modal de crear caso desde alerta
  const [showCreateCaseDialog, setShowCreateCaseDialog] = useState(false);
  const [alertForCase, setAlertForCase] = useState(null);
  const [showFullDetailDrawer, setShowFullDetailDrawer] = useState(false);
  const [fullDetailData, setFullDetailData] = useState(null);
  const [creatingCase, setCreatingCase] = useState(false);

  // Modal de asignación al mover tarjetas
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [pendingMove, setPendingMove] = useState(null); // {alertId, targetColumn}
  const [users, setUsers] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  
  // Selección múltiple de alertas
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [showMultiCaseDialog, setShowMultiCaseDialog] = useState(false);
  const [multiCaseData, setMultiCaseData] = useState({
    titulo: '',
    descripcion: '',
    prioridad: 'ALTA',
    tipo: 'ALERTA'
  });

  // DataTable sort state
  const [alertSortConfig, setAlertSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [alertColumnFilters, setAlertColumnFilters] = useState({});

  const handleAlertSort = useCallback((key) => {
    setAlertSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const handleAlertColumnFilter = useCallback((key, value) => {
    setAlertColumnFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const statusColumns = [
    { key: 'NUEVA', label: 'Nuevas', color: 'bg-red-50 border-red-200' },
    { key: 'TOMADA', label: 'Tomadas', color: 'bg-blue-50 border-blue-200' },
    { key: 'EN_INVESTIGACION', label: 'En Investigación', color: 'bg-indigo-50 border-indigo-200' },
    { key: 'ESCALADA', label: 'Escaladas', color: 'bg-purple-50 border-purple-200' },
    { key: 'PENDIENTE_APROBACION', label: 'Pend. Aprobación', color: 'bg-amber-50 border-amber-200' },
    { key: 'CERRADA', label: 'Cerradas', color: 'bg-green-50 border-green-200' },
    { key: 'DESCARTADA', label: 'Descartadas', color: 'bg-gray-50 border-gray-200' },
  ];

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    loadAlerts();
    loadUsersAndCurrentUser();
  }, []);

  // Load full detail for drawer
  useEffect(() => {
    if (showFullDetailDrawer && selectedAlert) {
      api.get(`/alerts/${selectedAlert.id}`)
        .then(r => setFullDetailData(r.data))
        .catch(() => setFullDetailData(null));
    }
  }, [showFullDetailDrawer, selectedAlert]);

  
  // Toggle selección de alerta
  const toggleAlertSelection = (alertId) => {
    setSelectedAlerts(prev => 
      prev.includes(alertId) 
        ? prev.filter(id => id !== alertId)
        : [...prev, alertId]
    );
  };
  
  // Crear caso con múltiples alertas
  const handleCreateMultiCase = async () => {
    if (selectedAlerts.length === 0) {
      toast.error('Seleccione al menos una alerta');
      return;
    }
    
    if (!multiCaseData.titulo.trim()) {
      toast.error('El título del caso es requerido');
      return;
    }
    
    setCreatingCase(true);
    try {
      // Obtener todas las cuentas únicas de las alertas seleccionadas
      const selectedAlertObjects = alerts.filter(a => selectedAlerts.includes(a.id));
      const cuentasRelacionadas = [...new Set(selectedAlertObjects.map(a => a.cuenta_id).filter(Boolean))];
      
      const newCase = {
        titulo: multiCaseData.titulo,
        descripcion: multiCaseData.descripcion || `Caso generado con ${selectedAlerts.length} alertas seleccionadas`,
        tipo: multiCaseData.tipo,
        prioridad: multiCaseData.prioridad,
        alertas_ids: selectedAlerts,
        cuentas_relacionadas: cuentasRelacionadas,
        tags: []
      };
      
      const response = await api.post('/cases', newCase);
      toast.success(`Caso creado con ${selectedAlerts.length} alertas`);
      setShowMultiCaseDialog(false);
      setSelectedAlerts([]);
      setMultiCaseData({ titulo: '', descripcion: '', prioridad: 'ALTA', tipo: 'ALERTA' });
      
      // Navegar al caso recién creado
      navigate(`/m/gestion-de-casos/caso/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear caso');
    } finally {
      setCreatingCase(false);
    }
  };

  const loadUsersAndCurrentUser = async () => {
    try {
      // Cargar usuario actual
      const meRes = await api.get('/auth/me');
      setCurrentUser(meRes.data);
      
      // Cargar lista de usuarios (solo si es admin)
      if (meRes.data.rol === 'ADMINISTRADOR') {
        const usersRes = await api.get('/users');
        setUsers(usersRes.data.filter(u => u.activo));
      }
    } catch (error) {
    }
  };

  const loadAlerts = async () => {
    try {
      const response = await api.get('/alerts?limit=200');
      setAlerts(response.data);
    } catch (error) {
      toast.error('Error al cargar alertas');
    } finally {
      setLoading(false);
    }
  };

  const [cerrandoViejas, setCerrandoViejas] = useState(false);
  const cerrarAlertasCasosCerrados = async () => {
    setCerrandoViejas(true);
    try {
      const prev = await api.post('/cases/mantenimiento/cerrar-alertas-casos-cerrados?dry_run=true');
      const emb = prev.data?.alertas_embebidas_a_cerrar || 0;
      const col = prev.data?.alertas_coleccion_a_cerrar || 0;
      const casos = prev.data?.casos_afectados || 0;
      const total = emb + col;
      if (total === 0) {
        toast.success('No hay alertas abiertas en casos cerrados. Todo al día ✅');
        return;
      }
      if (!window.confirm(`Se cerrarán ${total} alerta(s) que quedaron abiertas en ${casos} caso(s) ya cerrado(s). Esta acción no reabre casos. ¿Continuar?`)) return;
      const res = await api.post('/cases/mantenimiento/cerrar-alertas-casos-cerrados?dry_run=false');
      const cerradas = (res.data?.alertas_embebidas_cerradas || 0) + (res.data?.alertas_coleccion_cerradas || 0);
      toast.success(`${cerradas} alerta(s) cerradas en ${res.data?.casos_modificados || 0} caso(s).`);
      loadAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar alertas de casos cerrados');
    } finally {
      setCerrandoViejas(false);
    }
  };

  // Función para abrir el modal de vista rápida
  const openDetailModal = async (alert) => {
    setSelectedAlert(alert);
    setShowDetailDialog(true);
    try {
      const commentsRes = await api.get(`/alerts/${alert.id}/comments`);
      setAlertComments(commentsRes.data);
    } catch (error) {
      console.error('Error loading comments:', error);
      setAlertComments([]);
    }
  };

  // Navegar a la página de detalle completa
  const goToFullDetail = (alert) => {
    setShowDetailDialog(false);
    navigate(`/m/monitoreo-transaccional/alerts/${alert.id}`);
  };

  // Abrir diálogo para crear caso desde alerta
  const openCreateCaseDialog = (alert) => {
    setAlertForCase(alert);
    setShowCreateCaseDialog(true);
  };

  // Crear caso desde alerta
  const handleCreateCaseFromAlert = async () => {
    if (!alertForCase) return;
    
    setCreatingCase(true);
    try {
      // Determinar tipo de caso basado en severidad de la alerta
      let tipoCaso = 'ALERTA';
      if (alertForCase.severidad === 'CRITICA' || alertForCase.severidad === 'ALTA') {
        tipoCaso = 'FRAUDE';
      }
      
      // Determinar prioridad basada en severidad
      const prioridadMap = {
        'CRITICA': 'CRITICA',
        'ALTA': 'ALTA',
        'MEDIA': 'MEDIA',
        'BAJA': 'BAJA',
        'INFO': 'BAJA'
      };
      
      const newCase = {
        titulo: `Caso: ${alertForCase.titulo}`,
        descripcion: `Caso generado desde alerta ID: ${alertForCase.id}\n\n${alertForCase.descripcion}`,
        tipo: tipoCaso,
        prioridad: prioridadMap[alertForCase.severidad] || 'MEDIA',
        alerta_id: alertForCase.id,
        cuenta_id: alertForCase.cuenta_id,
      };
      
      const response = await api.post('/cases', newCase);
      toast.success('Caso creado exitosamente');
      setShowCreateCaseDialog(false);
      setAlertForCase(null);
      
      // Navegar al caso recién creado
      navigate(`/m/gestion-de-casos/casos?case=${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear caso');
    } finally {
      setCreatingCase(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      toast.info('Exportando alertas a CSV...');
      const response = await api.get('/exports/alerts/csv', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `alertas_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Alertas exportadas correctamente');
    } catch (error) {
      toast.error('Error al exportar alertas');
    }
  };

  // Reabrir alerta cerrada
  const handleReopenAlert = async (alert) => {
    try {
      await api.patch(`/alerts/${alert.id}`, { estado: 'EN_INVESTIGACION' });
      toast.success('Alerta reabierta correctamente');
      loadAlerts();
    } catch (error) {
      toast.error('Error al reabrir alerta');
    }
  };

  // Crear alerta relacionada/duplicada
  const handleDuplicateAlert = async (alert) => {
    try {
      await api.post('/alerts/', {
        titulo: `[Seguimiento] ${alert.titulo}`,
        descripcion: `Seguimiento de alerta anterior (ID: ${alert.id})\n\nDescripción original:\n${alert.descripcion}`,
        severidad: alert.severidad,
        monitor_id: alert.monitor_id,
        cuenta_id: alert.cuenta_id,
        alerta_padre_id: alert.id,
      });
      toast.success('Alerta relacionada creada correctamente');
      loadAlerts();
    } catch (error) {
      toast.error('Error al crear alerta relacionada');
    }
  };

  // Drag & Drop handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    try {
      // Encontrar la columna destino
      const activeAlert = alerts.find(a => a.id === active.id);
      if (!activeAlert) return;

      // Determinar la columna destino basándonos en over.id
      let targetColumn = null;
      
      // Si over.id es una alerta, encontrar su columna
      const overAlert = alerts.find(a => a.id === over.id);
      if (overAlert) {
        targetColumn = overAlert.estado;
      } else {
        // Si no es una alerta, verificar si es el ID de la columna
        const column = statusColumns.find(c => c.key === over.id);
        if (column) {
          targetColumn = column.key;
        }
      }

      // Si no hay cambio de columna, no hacer nada
      if (!targetColumn || activeAlert.estado === targetColumn) return;

      // Verificar si la alerta NO está asignada
      if (!activeAlert.asignado_a) {
        // Si es admin, mostrar diálogo para elegir a quién asignar
        if (currentUser?.rol === 'ADMINISTRADOR') {
          setPendingMove({ alertId: active.id, targetColumn, alert: activeAlert });
          setSelectedAssignee('');
          setShowAssignDialog(true);
          return;
        } else {
          // Si es usuario normal, auto-asignar al usuario actual
          try {
            await api.patch(`/alerts/${active.id}`, { 
              estado: targetColumn,
              asignado_a: currentUser?.id 
            });
            setAlerts(prev => prev.map(alert => 
              alert.id === active.id 
                ? { ...alert, estado: targetColumn, asignado_a: currentUser?.id, asignado_a_nombre: currentUser?.nombre }
                : alert
            ));
            toast.success(`Alerta movida y asignada a ti`);
            return;
          } catch (error) {
            toast.error('Error al actualizar alerta');
            loadAlerts();
            return;
          }
        }
      }

      // Si ya está asignada, solo mover
      await api.patch(`/alerts/${active.id}`, { estado: targetColumn });
      
      // Actualizar localmente para feedback inmediato
      setAlerts(prev => prev.map(alert => 
        alert.id === active.id 
          ? { ...alert, estado: targetColumn }
          : alert
      ));
      
      toast.success(`Alerta movida a ${statusColumns.find(c => c.key === targetColumn)?.label}`);
    } catch (error) {
      console.error('Error en drag end:', error);
      toast.error('Error al actualizar estado de alerta');
      loadAlerts(); // Recargar para sincronizar
    }
  };

  // Confirmar asignación desde diálogo
  const handleConfirmAssignment = async () => {
    if (!pendingMove) return;
    
    const { alertId, targetColumn } = pendingMove;
    const assigneeId = selectedAssignee || currentUser?.id;
    
    try {
      await api.patch(`/alerts/${alertId}`, { 
        estado: targetColumn,
        asignado_a: assigneeId 
      });
      
      const assigneeName = users.find(u => u.id === assigneeId)?.nombre || currentUser?.nombre;
      
      setAlerts(prev => prev.map(alert => 
        alert.id === alertId 
          ? { ...alert, estado: targetColumn, asignado_a: assigneeId, asignado_a_nombre: assigneeName }
          : alert
      ));
      
      toast.success(`Alerta movida y asignada a ${assigneeName}`);
    } catch (error) {
      toast.error('Error al asignar alerta');
      loadAlerts();
    } finally {
      setShowAssignDialog(false);
      setPendingMove(null);
      setSelectedAssignee('');
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeAlert = alerts.find(a => a.id === active.id);
    const overAlert = alerts.find(a => a.id === over.id);

    if (!activeAlert) return;

    // Si estamos sobre otra alerta en diferente columna
    if (overAlert && activeAlert.estado !== overAlert.estado) {
      // No necesitamos hacer nada aquí, el cambio real se hace en dragEnd
    }
  };

  const activeAlert = activeId ? alerts.find(a => a.id === activeId) : null;

  // Filtered & sorted alerts for DataTable
  const filteredAlerts = useMemo(() => {
    let result = [...alerts];
    if (filterUser !== 'all') {
      if (filterUser === 'unassigned') {
        result = result.filter(a => !a.asignado_a);
      } else {
        result = result.filter(a => a.asignado_a === filterUser);
      }
    }
    if (filterSeverity !== 'all') result = result.filter(a => a.severidad === filterSeverity);
    if (filterStatus !== 'all') result = result.filter(a => a.estado === filterStatus);
    if (searchAlertText.trim()) {
      const q = searchAlertText.toLowerCase();
      result = result.filter(a =>
        (a.titulo || '').toLowerCase().includes(q) ||
        (a.cuenta_id || '').toLowerCase().includes(q) ||
        (a.razon_social || '').toLowerCase().includes(q)
      );
    }
    // Apply column-level filters
    Object.entries(alertColumnFilters).forEach(([key, value]) => {
      if (!value || value === '' || value === 'all') return;
      if (typeof value === 'object' && (value.from || value.to)) {
        result = result.filter(row => {
          const d = row.created_at ? new Date(row.created_at).toISOString().split('T')[0] : '';
          if (value.from && d < value.from) return false;
          if (value.to && d > value.to) return false;
          return true;
        });
      } else {
        const q = String(value).toLowerCase();
        result = result.filter(row => String(row[key] || '').toLowerCase().includes(q));
      }
    });
    // Sort
    if (alertSortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[alertSortConfig.key] || '';
        const bVal = b[alertSortConfig.key] || '';
        const cmp = String(aVal).localeCompare(String(bVal));
        return alertSortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [alerts, filterUser, filterSeverity, filterStatus, searchAlertText, alertColumnFilters, alertSortConfig]);

  // Column definitions for alerts DataTable
  const alertTableColumns = [
    {
      key: 'severidad',
      label: 'Severidad',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'CRITICA', label: 'Crítica' },
        { value: 'ALTA', label: 'Alta' },
        { value: 'MEDIA', label: 'Media' },
        { value: 'BAJA', label: 'Baja' },
      ],
      width: 110,
      render: (row) => (
        <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold ${getSeverityColor(row.severidad)}`}>
          {row.severidad}
        </span>
      ),
    },
    {
      key: 'estado',
      label: 'Estado',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'NUEVA', label: 'Nueva' },
        { value: 'TOMADA', label: 'Tomada' },
        { value: 'EN_INVESTIGACION', label: 'En Investigación' },
        { value: 'ESCALADA', label: 'Escalada' },
        { value: 'CERRADA', label: 'Cerrada' },
        { value: 'DESCARTADA', label: 'Descartada' },
      ],
      width: 140,
      render: (row) => (
        <div className="flex items-center gap-1">
          <Badge variant="outline" className="text-xs">{row.estado?.replace(/_/g, ' ')}</Badge>
          {row.estado === 'PENDIENTE_APROBACION' && <Clock size={12} className="text-amber-600 animate-pulse" />}
        </div>
      ),
    },
    {
      key: 'titulo',
      label: 'Título',
      sortable: true,
      filterable: true,
      render: (row) => (
        <div className="min-w-0">
          <div className="flex items-center gap-1">
            <span className="font-medium text-slate-900 truncate">{row.titulo}</span>
            {row.alerta_padre_id && (
              <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 flex-shrink-0">Rel</Badge>
            )}
          </div>
          {row.descripcion && (
            <p className="text-xs text-slate-500 truncate mt-0.5">{row.descripcion}</p>
          )}
        </div>
      ),
    },
    {
      key: 'razon_social',
      label: 'Cuenta',
      sortable: true,
      filterable: true,
      defaultVisible: true,
      render: (row) => (
        <div className="text-sm">
          {row.razon_social && <span className="font-medium text-slate-700">{row.razon_social}</span>}
          {row.cuenta_id && <p className="text-xs font-mono text-slate-400">{row.cuenta_id}</p>}
        </div>
      ),
    },
    {
      key: 'asignado_a_nombre',
      label: 'Asignado',
      sortable: true,
      filterable: true,
      defaultVisible: true,
      render: (row) => (
        <span className="text-sm text-slate-600">
          {row.asignado_a_nombre || <span className="text-slate-400 italic">Sin asignar</span>}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Fecha',
      sortable: true,
      filterable: true,
      filterType: 'date',
      width: 120,
      render: (row) => (
        <span className="text-xs text-slate-500 font-mono">
          {row.created_at ? new Date(row.created_at).toLocaleDateString('es-ES') : '-'}
        </span>
      ),
    },
    {
      key: 'acciones',
      label: '',
      width: 80,
      alwaysVisible: true,
      resizable: false,
      render: (row) => {
        const isClosed = row.estado === 'CERRADA' || row.estado === 'DESCARTADA';
        const isActive = !isClosed;
        return (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openDetailModal(row)} data-testid={`view-alert-${row.id}`}>
              <Eye size={14} />
            </Button>
            {isActive && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600" onClick={() => openCreateCaseDialog(row)} data-testid={`create-case-${row.id}`}>
                <Briefcase size={14} />
              </Button>
            )}
            {isClosed && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600" onClick={() => handleReopenAlert(row)}>
                <RotateCcw size={14} />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando alertas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto">
      {/* Barra de selección múltiple */}
      {selectedAlerts.length > 0 && (
        <div className="bg-blue-600 text-white px-4 py-3 rounded-lg mb-4 flex items-center justify-between shadow-lg animate-in slide-in-from-top-2">
          <div className="flex items-center gap-3">
            <CheckSquare size={20} />
            <span className="font-medium">
              {selectedAlerts.length} alerta{selectedAlerts.length > 1 ? 's' : ''} seleccionada{selectedAlerts.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setSelectedAlerts([])}
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              Limpiar selección
            </Button>
            <Button
              size="sm"
              onClick={() => setShowMultiCaseDialog(true)}
              className="bg-white text-blue-600 hover:bg-blue-50"
              data-testid="create-multi-case-btn"
            >
              <Briefcase size={16} className="mr-2" />
              Crear Caso con {selectedAlerts.length} Alertas
            </Button>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">
            Gestión de Alertas
          </h1>
          <p className="text-slate-600 mt-1">
            {alerts.length} alertas en el sistema • Selecciona alertas para crear casos
          </p>
        </div>
        <div className="flex items-center space-x-2 mt-4 lg:mt-0">
          <Button
            data-testid="toggle-dashboard-btn"
            variant={showDashboard ? "default" : "outline"}
            size="sm"
            onClick={() => setShowDashboard(!showDashboard)}
          >
            <BarChart3 size={16} className="mr-2" />
            Dashboard
          </Button>
          {currentUser?.rol === 'ADMINISTRADOR' && (
            <Button
              data-testid="cerrar-alertas-casos-cerrados-btn"
              variant="outline"
              size="sm"
              onClick={cerrarAlertasCasosCerrados}
              disabled={cerrandoViejas}
              title="Cierra retroactivamente las alertas que quedaron abiertas en casos ya cerrados"
              style={{ color: '#475569' }}
            >
              <CheckCheck size={16} className="mr-2" style={{ color: '#475569' }} />
              <span style={{ color: '#475569' }}>{cerrandoViejas ? 'Cerrando…' : 'Cerrar alertas de casos cerrados'}</span>
            </Button>
          )}
          <Button
            data-testid="export-csv-button"
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            style={{ color: '#475569' }}
          >
            <Download size={16} className="mr-2" style={{ color: '#475569' }} />
            <span style={{ color: '#475569' }}>Exportar CSV</span>
          </Button>
          <ExcelExportButton type="alerts" label="Excel" filters={{ estado: filterStatus !== 'all' ? filterStatus : '', severidad: filterSeverity !== 'all' ? filterSeverity : '' }} />
          
          {/* Filtro por Usuario Asignado */}
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger className="w-[200px]" data-testid="filter-user-select">
              <User size={16} className="mr-2 text-slate-400" />
              <SelectValue placeholder="Filtrar por usuario" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Todos los usuarios</SelectItem>
              <SelectItem value="unassigned">Sin asignar</SelectItem>
              {currentUser && (
                <SelectItem value={currentUser.id}>Mis alertas</SelectItem>
              )}
              {currentUser?.rol === 'ADMINISTRADOR' && users.map(user => (
                user.id !== currentUser?.id && (
                  <SelectItem key={user.id} value={user.id}>{user.nombre}</SelectItem>
                )
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Dashboard Panel */}
      {showDashboard && <AlertsDashboard onClose={() => setShowDashboard(false)} />}

      {/* Filters Row */}
      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={filterSeverity} onValueChange={setFilterSeverity}>
          <SelectTrigger className="w-[180px]" data-testid="filter-severity-select">
            <SelectValue placeholder="Severidad" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Todas las severidades</SelectItem>
            <SelectItem value="CRITICA">Crítica</SelectItem>
            <SelectItem value="ALTA">Alta</SelectItem>
            <SelectItem value="MEDIA">Media</SelectItem>
            <SelectItem value="BAJA">Baja</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]" data-testid="filter-status-select">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="NUEVA">Nueva</SelectItem>
            <SelectItem value="EN_REVISION">En Revisión</SelectItem>
            <SelectItem value="EN_INVESTIGACION">En Investigación</SelectItem>
            <SelectItem value="ESCALADA">Escalada</SelectItem>
            <SelectItem value="CERRADA">Cerrada</SelectItem>
            <SelectItem value="DESCARTADA">Descartada</SelectItem>
          </SelectContent>
        </Select>

        <Input
          placeholder="Buscar por título, cuenta o razón social..."
          className="w-[280px]"
          data-testid="search-alerts-input"
          value={searchAlertText}
          onChange={(e) => setSearchAlertText(e.target.value)}
        />
      </div>

      {/* List View - DataTable */}
      <Card className="border border-border p-4" style={{ backgroundColor: '#ffffff' }}>
        <DataTable
          columns={alertTableColumns}
          data={filteredAlerts}
          loading={loading}
          emptyMessage={filterUser !== 'all' || filterSeverity !== 'all' || filterStatus !== 'all' || searchAlertText ? 'No hay alertas que coincidan con los filtros' : 'No hay alertas en el sistema'}
          emptyIcon={<AlertTriangle size={32} />}
          sortConfig={alertSortConfig}
          onSort={handleAlertSort}
          columnFilters={alertColumnFilters}
          onColumnFilterChange={handleAlertColumnFilter}
          checkboxSelection={true}
          selectedIds={selectedAlerts}
          onToggleSelect={toggleAlertSelection}
          onToggleSelectAll={() => {
            if (selectedAlerts.length === filteredAlerts.length) {
              setSelectedAlerts([]);
            } else {
              setSelectedAlerts(filteredAlerts.map(a => a.id));
            }
          }}
          onRowClick={(row) => openDetailModal(row)}
        />
      </Card>

      {/* All dialogs extracted to AlertsDialogs */}
      <AlertsDialogs
        getSeverityColor={getSeverityColor}
        showDetailDialog={showDetailDialog} setShowDetailDialog={setShowDetailDialog}
        selectedAlert={selectedAlert} alertComments={alertComments} loadAlerts={loadAlerts}
        showFullDetailDrawer={showFullDetailDrawer} setShowFullDetailDrawer={setShowFullDetailDrawer}
        fullDetailData={fullDetailData}
        showCreateCaseDialog={showCreateCaseDialog} setShowCreateCaseDialog={setShowCreateCaseDialog}
        alertForCase={alertForCase} setAlertForCase={setAlertForCase}
        handleCreateCaseFromAlert={handleCreateCaseFromAlert} creatingCase={creatingCase}
        showAssignDialog={showAssignDialog} setShowAssignDialog={setShowAssignDialog}
        pendingMove={pendingMove} setPendingMove={setPendingMove}
        selectedAssignee={selectedAssignee} setSelectedAssignee={setSelectedAssignee}
        handleConfirmAssignment={handleConfirmAssignment}
        users={users} currentUser={currentUser}
        showMultiCaseDialog={showMultiCaseDialog} setShowMultiCaseDialog={setShowMultiCaseDialog}
        selectedAlerts={selectedAlerts} alerts={alerts} toggleAlertSelection={toggleAlertSelection}
        multiCaseData={multiCaseData} setMultiCaseData={setMultiCaseData}
        handleCreateMultiCase={handleCreateMultiCase}
        openCreateCaseDialog={openCreateCaseDialog}
      />
    </div>
  );
};

export default Alerts;
